import { useEffect, useMemo, useState } from 'react';

import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { z } from 'zod';

import { ApiError } from '@mds/shared/lib/api';
import type { AdminOtpChallenge } from '@mds/shared/types/admin-auth';

import AuthShell, { AuthError } from '@/components/auth/auth-shell';
import Button from '@/components/ui/button';
import Field, { inputCls } from '@/components/ui/field';
import * as auth from '@/auth/admin-auth.api';
import { useSession } from '@/auth/use-session';
import { REQUIRED, requiredEmail, useValidation } from '@/lib/validation';

/** Seconds until `iso`, floored at zero. */
const secondsUntil = (iso: string) => Math.max(0, Math.ceil((Date.parse(iso) - Date.now()) / 1000));

/** What the API takes for the first step. The password is only checked for length here. */
const credentialsSchema = z.object({
  email: requiredEmail,
  password: z
    .string()
    .min(1, REQUIRED)
    .max(128, 'That is longer than any password this system accepts.'),
});

/** The emailed code: exactly six digits. */
const codeSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1, REQUIRED)
    .regex(/^\d{6}$/, 'Enter the six digits from the email.'),
});

/**
 * Login is two steps against one screen: credentials, then the emailed code.
 *
 * The API deliberately gives the same answer for an unknown account, a wrong
 * password and a locked one, so this never tries to be more specific than the
 * message it is handed.
 */
export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { status, refresh } = useSession();

  const [challenge, setChallenge] = useState<AdminOtpChallenge | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const credentials = useMemo(() => ({ email, password }), [email, password]);
  const credentialsCheck = useValidation(credentialsSchema, credentials);
  const codeDraft = useMemo(() => ({ code }), [code]);
  const codeCheck = useValidation(codeSchema, codeDraft);

  // The resend button unlocks on a timer the server sets, so it is counted
  // down here rather than guessed at.
  useEffect(() => {
    if (!challenge) return;
    setCooldown(secondsUntil(challenge.resendAvailableAt));
    const id = setInterval(() => setCooldown((n) => (n <= 1 ? 0 : n - 1)), 1000);
    return () => clearInterval(id);
  }, [challenge]);

  if (status === 'authenticated') {
    const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;
    return <Navigate to={from ?? '/'} replace />;
  }

  const fail = (e: unknown, fallback: string) =>
    setError(e instanceof ApiError ? e.message : fallback);

  const submitCredentials = async () => {
    if (!credentialsCheck.check()) return;

    setBusy(true);
    setError(null);
    try {
      setChallenge(await auth.login(email.trim(), password));
      setCode('');
      codeCheck.reset();
    } catch (e) {
      fail(e, 'Could not reach the server. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async () => {
    if (!challenge || !codeCheck.check()) return;

    setBusy(true);
    setError(null);
    try {
      await auth.verifyOtp(challenge.challengeId, code.trim());
      // The cookie is set; /me is the single source of truth for who we are.
      await refresh();
      const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;
      navigate(from ?? '/', { replace: true });
    } catch (e) {
      fail(e, 'Could not verify that code. Try again.');
      setBusy(false);
    }
  };

  const resend = async () => {
    if (!challenge) return;
    setBusy(true);
    setError(null);
    try {
      setChallenge(await auth.resendOtp(challenge.challengeId));
      setCode('');
      codeCheck.reset();
    } catch (e) {
      fail(e, 'Could not send another code.');
    } finally {
      setBusy(false);
    }
  };

  if (challenge) {
    return (
      <AuthShell
        title="Check your email"
        hint={`We sent a code to ${challenge.destination}. It expires in a few minutes.`}
        footer={
          <button
            type="button"
            onClick={() => {
              setChallenge(null);
              setError(null);
            }}
            className="text-ink-muted underline underline-offset-4 hover:text-brand-text"
          >
            Use a different account
          </button>
        }
      >
        <form
          className="flex flex-col gap-5"
          onSubmit={(e) => {
            e.preventDefault();
            void submitCode();
          }}
        >
          <Field label="Six-digit code" required {...codeCheck.field('code')}>
            <input
              className={inputCls + ' text-center font-mono text-xl tracking-brand'}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              autoFocus
              aria-label="Six-digit code"
            />
          </Field>

          {error && <AuthError>{error}</AuthError>}

          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? 'Verifying…' : 'Verify and sign in'}
          </Button>

          <Button
            type="button"
            variant="quiet"
            className="w-full"
            disabled={busy || cooldown > 0}
            onClick={() => void resend()}
          >
            {cooldown > 0 ? `Send another code in ${cooldown}s` : 'Send another code'}
          </Button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Sign in"
      hint="MDS administration. Staff accounts only."
      footer={
        <Link
          to="/forgot-password"
          className="text-ink-muted underline underline-offset-4 hover:text-brand-text"
        >
          Forgotten your password?
        </Link>
      }
    >
      <form
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          void submitCredentials();
        }}
      >
        <Field label="Email" required {...credentialsCheck.field('email')}>
          <input
            type="email"
            className={inputCls}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            autoFocus
          />
        </Field>

        <Field label="Password" required {...credentialsCheck.field('password')}>
          <input
            type="password"
            className={inputCls}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
        </Field>

        {error && <AuthError>{error}</AuthError>}

        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? 'Checking…' : 'Continue'}
        </Button>
      </form>
    </AuthShell>
  );
}
